<script setup lang="ts">
/*
 * First-visit consent: a flat ink sheet that rises at the bottom centre
 * (above the phone tab bar). Non-modal on purpose: the page stays usable
 * behind it. "Customize" expands the same toggles the cookie policy page uses
 * (CookiePreferences), inline.
 */
import { onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { Cookie } from 'lucide-vue-next';
import { defaultConsent, loadConsent, saveConsent } from '../utils/consent';
import CookiePreferences from './content/CookiePreferences.vue';

const visible = ref(false);
const customizing = ref(false);

onMounted(() => {
  visible.value = loadConsent() === null;
});

function acceptAll() {
  saveConsent({ essential: true, analytics: true, marketing: true, decidedAt: new Date().toISOString() });
  visible.value = false;
}

function rejectNonEssential() {
  saveConsent({ ...defaultConsent(), decidedAt: new Date().toISOString() });
  visible.value = false;
}
</script>

<template>
  <section
    v-if="visible"
    :aria-label="$t('cookieBanner.label')"
    class="animate-rise fixed inset-x-3 bottom-[calc(var(--bottom-tabs-h)+12px)] z-40 mx-auto flex max-h-[calc(100vh-6rem)] max-w-[760px] flex-col gap-4 overflow-y-auto rounded-xl border border-border-strong bg-bg-base p-5 md:bottom-6"
  >
    <div class="flex items-start gap-4">
      <span class="grid size-11 shrink-0 place-items-center rounded-full bg-accent text-text-inv">
        <Cookie class="size-5" aria-hidden="true" />
      </span>
      <p class="text-[14px] leading-[22px] text-text-dim">
        {{ $t('cookieBanner.body') }}
        <RouterLink to="/legal/cookies" class="font-semibold text-link hover:underline">{{ $t('cookieBanner.policy') }}</RouterLink>
      </p>
    </div>

    <div v-if="customizing" class="border-t border-border pt-4">
      <CookiePreferences compact @saved="visible = false" />
    </div>

    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <button type="button" class="btn-ghost" :aria-expanded="customizing" @click="customizing = !customizing">{{ $t('cookieBanner.customize') }}</button>
      <button type="button" class="btn-secondary" @click="rejectNonEssential">{{ $t('cookieBanner.essentialOnly') }}</button>
      <button type="button" class="btn-primary" @click="acceptAll">{{ $t('cookieBanner.acceptAll') }}</button>
    </div>
  </section>
</template>
